import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Callout } from "@freightsimple/ui-core/Display/Callout";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { PaymentsMethodsDropdown } from "@/Shared/Payments/Dropdowns/PaymentsMethodsDropdown";
import { PaymentsSummaryBox } from "../CustomComponents/PaymentsSummaryBox";
import { PaymentsSummaryItem } from "../CustomComponents/PaymentsSummaryItem";
import {
  paymentsChargeTimingText,
  paymentsMethodInstructions,
  paymentsNeedsReference,
} from "../Helpers/paymentsMakePaymentsMethodText";
import type {
  InvoiceSummary,
  PaymentMethodsState,
} from "../Types/paymentsPaymentTypes";

interface PaymentsMakePaymentsReviewPanelProps {
  /** The payment methods are still being fetched — the method picker shows a
   *  loading row instead. The totals never wait; they derive from the
   *  selection, not the fetch. */
  loading: boolean;
  /** Set when the payment methods failed to load: the picker becomes an
   *  error with a Try again button that calls this. */
  onRetryFailedLoad?: (() => void) | undefined;
  paymentMethodsState: PaymentMethodsState;
  selectedPaymentMethod: string | undefined;
  /** Raw dropdown selection — the flow intercepts "add-credit-card" itself. */
  onPaymentMethodSelected: (paymentMethodId: string) => void;
  paymentReference: string;
  onPaymentReferenceChange: (paymentReference: string) => void;
  currency: string;
  totalAmountToPay: number;
  invoiceSummary: InvoiceSummary;
}

/**
 * The review side of the Make Payments dialog: pick a method, see what
 * confirming will do, and check the totals. The confirm/cancel actions live
 * in the dialog's own footer, not here.
 */
export function PaymentsMakePaymentsReviewPanel({
  loading,
  onRetryFailedLoad,
  paymentMethodsState,
  selectedPaymentMethod,
  onPaymentMethodSelected,
  paymentReference,
  onPaymentReferenceChange,
  currency,
  totalAmountToPay,
  invoiceSummary,
}: PaymentsMakePaymentsReviewPanelProps) {
  const instructions = paymentsMethodInstructions(
    paymentMethodsState,
    selectedPaymentMethod,
  );

  const methodPicker = loading ? (
    <Row gap={2}>
      <Spinner size={20} />
      <T size="base" color="muted">
        Loading payment methods…
      </T>
    </Row>
  ) : onRetryFailedLoad !== undefined ? (
    <Banner
      variant="danger"
      title="We couldn't load your payment methods"
      actions={
        <Button onClick={onRetryFailedLoad} data-testid="payment-methods-retry">
          Try again
        </Button>
      }
    >
      Something went wrong on our end. Please try again.
    </Banner>
  ) : (
    <>
      <FormItem label="Payment Method" fillWidth>
        <PaymentsMethodsDropdown
          cards={paymentMethodsState.creditCards}
          bankAccounts={paymentMethodsState.bankAccounts}
          isChequePaymentPermitted={paymentMethodsState.isChequePermitted}
          isEFTPaymentPermitted={paymentMethodsState.isEFTPermitted}
          isETransferPaymentPermitted={paymentMethodsState.isETransferPermitted}
          isWirePaymentPermitted={paymentMethodsState.isWirePermitted}
          selectedStripePaymentMethodId={selectedPaymentMethod}
          onOptionSelected={onPaymentMethodSelected}
        />
      </FormItem>

      <Callout>
        <T size="base" color="secondary">
          {paymentsChargeTimingText(paymentMethodsState, selectedPaymentMethod)}
        </T>
      </Callout>

      {paymentsNeedsReference(paymentMethodsState, selectedPaymentMethod) && (
        <Stack gap={3} align="stretch">
          {instructions !== undefined && (
            <T size="base" color="muted">
              {instructions}
            </T>
          )}
          <FormItem
            label="(Optional) If you have a payment reference number, enter it below"
            fillWidth
          >
            <TextInput
              type="text"
              value={paymentReference}
              onChange={(e) => onPaymentReferenceChange(e.target.value)}
              width="full"
            />
          </FormItem>
        </Stack>
      )}
    </>
  );

  return (
    <Panel title="Payment Review">
      <Stack gap={5} align="stretch">
        {methodPicker}

        <PaymentsSummaryBox>
          <T size="lg" weight="medium">
            Payment Summary
          </T>
          <PaymentsSummaryItem>
            Shipment Charge ({invoiceSummary.shipment.count}):{" "}
            <T size="lg" weight="medium" color="secondary">
              ${invoiceSummary.shipment.total.toFixed(2)}
            </T>
          </PaymentsSummaryItem>
          <PaymentsSummaryItem>
            Additional Charge ({invoiceSummary.additional.count}):{" "}
            <T size="lg" weight="medium" color="secondary">
              ${invoiceSummary.additional.total.toFixed(2)}
            </T>
          </PaymentsSummaryItem>

          <Divider spacing="sm" />

          <PaymentsSummaryItem>
            TOTAL:{" "}
            <Row gap={1}>
              <T size="lg" weight="medium" color="secondary" nowrap>
                ${totalAmountToPay}
              </T>
              <T size="lg" weight="medium" color="muted" nowrap>
                {currency}
              </T>
            </Row>
          </PaymentsSummaryItem>
        </PaymentsSummaryBox>
      </Stack>
    </Panel>
  );
}
